<template>
  <div class="task-page-container">
    <div class="task-page">
      <svg width="0" height="0" style="position: absolute; visibility: hidden;">
        <defs>
          <linearGradient id="star-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" style="stop-color: #ff8e8e; stop-opacity: 1" />
            <stop offset="100%" style="stop-color: #aa341b; stop-opacity: 1" />
          </linearGradient>
        </defs>
      </svg>
      <!-- 季度目标卡片 -->
      <div class="summary-card">
        <div class="summary-top">
          <div class="title-row">
            <span class="summary-title">{{ $t('task_quarter_progress') }}</span>
            <div class="detail-btn" @click="goToSummaryDetail">
              <span>{{ $t('task_view_detail') }}</span>
              <Icon name="arrow" />
            </div>
          </div>
          <span class="summary-date">{{ $t('task_quarter_deadline') }}{{ quarterEndDate }}</span>
        </div>
        <div class="summary-note">{{ $t('task_kpi_updated_yesterday') }}</div>
        <div class="summary-metrics">
          <div class="metric-item">
            <span class="metric-value">{{ quarterlyCompletedCount ? quarterlyCompletedCount : 0 }}</span>
            <span class="metric-label">{{ $t('task_completed_forward') }}</span>
          </div>
          <div class="metric-divider"></div>
          <div class="metric-item">
            <span class="metric-value">{{ quarterlyTargetCount }}</span>
            <span class="metric-label">{{ $t('task_target_forward') }}</span>
          </div>
          <div class="metric-chart">
            <van-circle
              v-model="currentRate"
              :rate="quarterlyProgress"
              :speed="100"
              :text="currentRate.toFixed(0) + '%'"
              :stroke-width="60"
              layer-color="#f5f5f5"
              color="#aa341b"
              size="46px"
            />
          </div>
        </div>
      </div>

      <div class="header-row">
        <div class="title-row">
          <!-- <div class="page-title">本周任务</div> -->
          <div class="view-all-articles" @click="goToContent">
            <span class="icon-circle"><van-icon name="newspaper" /></span>
            <span>{{ $t('task_view_all_articles') }}</span>
            <Icon name="arrow" />
          </div>
        </div>
      </div>
      <div class="filter-row">
        <div class="product-filter">
          <van-dropdown-menu direction="down">
            <van-dropdown-item
              v-model="selectedProduct"
              :options="productOptions"
              :placeholder="$t('task_filter_product')"
            />
          </van-dropdown-menu>
        </div>
        <div class="event-filter">
          <van-dropdown-menu direction="down">
            <van-dropdown-item
              v-model="selectedEvent"
              :options="eventOptionsWithCount"
              :placeholder="$t('task_filter_task')"
            >
              <template #title>
                <div class="event-dropdown-title">
                  <span class="task-title">{{ getEventDisplayText() }}</span>
                  <div v-if="getTotalTaskCount() > 0" class="task-count-badge">
                    {{ getTotalTaskCount() }}
                  </div>
                </div>
              </template>
            </van-dropdown-item>
          </van-dropdown-menu>
        </div>
      </div>

      <!-- 其他内容保持不变 -->
      <van-list
        v-model="listLoading"
        :finished="finished"
        :finished-text="$t('task_no_more_task')"
        class="task-list"
        @load="onLoad"
      >
        <div v-for="group in groupedTasks" :key="group.id" class="task-group-card">
          <!-- Group Header (Event Info) -->
          <div class="group-header">
            <div class="header-row-1">
              <div class="left-badges">
                <span class="task-type" :class="group.event_type">{{ eventTypeText(group.event_type) }}{{ $t('task_type_task') }}</span>
                <span v-if="group.product_name && group.event_type != 'hcp-survey'" class="product-tag">{{ group.product_name }}</span>
              </div>
              <div class="right-date">
                <span v-if="group.event_end_date">{{ formatDate(group.event_end_date) }} {{ $t('task_deadline').replace('：', '') }}</span>
              </div>
            </div>

            <div class="header-row-2">
              <span class="group-title">{{ group.campaign_event_name }}</span>
              <div v-if="group.items[0].content_id && ['conference', 'content', 'segment', 'survey', 'hcp-survey'].includes(group.event_type)" class="preview-btn" @click="previewTask(group.items[0])">
                <Icon name="eye-o" />
                <span>{{ $t('task_preview') }}</span>
              </div>
            </div>

            <div v-if="group.reasonTarget" class="header-row-3">
              <span class="reason-label">{{ $t('task_recommend_reason') }}</span>
              <span class="reason-text">
                {{ group.reasonTarget }}。<span v-if="group.reasonPrior">{{ $t('task_star_indicate').split('★')[0] }}<font-awesome-icon icon="star" class="reason-star-icon" />{{ $t('task_star_indicate').split('★')[1] || '' }}{{ group.reasonPrior }}</span>
              </span>
            </div>
          </div>

          <!-- Physician Items -->
          <div class="group-items">
            <div v-for="(task, index) in group.visibleItems" :key="task.campaign_event_id + '_' + task.physician_id + '_' + index">

              <div class="physician-item">
                <div v-if="task.is_optional && task.execution_type === null" class="ignore-btn-icon" @click.stop="ignoreTask(task)">
                  <Icon name="cross" />
                </div>
                <div class="item-main-row">
                  <!-- Avatar Column -->
                  <div class="avatar-column">
                    <img :src="task.sex === '男' ? portraitMale : task.sex === '女' ? portraitFemale : portrait" class="avatar" />
                    <div v-if="task.priority === 1" class="priority-overlay">
                      <font-awesome-icon icon="star" class="priority-icon" />
                    </div>
                  </div>

                  <!-- Physician Info -->
                  <div class="physician-info-wrapper">
                    <div class="user-row">
                      <span v-if="task.is_favorite" class="fav-star"><Icon name="star" /></span>
                      <span class="user-name" @click.stop="goToDetail(task)">{{ task.physician_name }}</span>
                      <img v-if="task.is_wechat" class="weixin-icon" src="@/assets/svg/weixin.svg" />

                      <!-- Labels -->
                      <div v-if="task.physician_segment || task.physician_class" class="product-badge">
                        <div class="product-badge-top">
                          <div v-if="task.physician_class" class="segment-circle">
                            {{ task.physician_class }}
                          </div>
                          <div v-if="task.physician_segment" class="segment-text">
                            {{ task.physician_segment }}
                          </div>
                        </div>
                        <div class="product-name">
                          {{ task.product_name }}
                        </div>
                      </div>
                    </div>
                    <div class="hospital-row">
                      <div class="dept">{{ task.department_name }} {{ task.professional_title }}</div>
                      <div class="hosp">{{ task.institution_name }}</div>
                    </div>
                  </div>

                  <!-- Action Buttons -->
                  <div class="action-column">
                    <template v-if="task.execution_type === null">
                      <div v-if="task.event_type === 'conference'" class="action-item" @click.stop="sendTask(task)">
                        <div class="action-pill primary">
                          <font-awesome-icon :icon="['fas', 'paper-plane']" />
                          <span>{{ $t('task_invite') }}</span>
                        </div>
                      </div>

                      <div v-if="task.event_type === 'content' || task.event_type === 'hcp-survey'" class="action-item" @click.stop="sendTask(task)">
                        <div class="action-pill primary">
                          <font-awesome-icon :icon="['fas', 'paper-plane']" />
                          <span>{{ $t('task_send') }}</span>
                        </div>
                      </div>
                      <div v-if="task.event_type === 'visit' || task.event_type === 'call'" class="action-item" @click.stop="planVisit(task)">
                        <div class="action-pill primary">
                          <Icon name="notes-o" style="margin-right: 3px; font-size: 14px;" />
                          <span>{{ $t('task_visit') }}</span>
                        </div>
                      </div>
                      <div v-if="task.event_type === 'segment' || task.event_type === 'survey'" class="action-pill primary" @click.stop="fillFormTask(task)">
                        <font-awesome-icon :icon="['fas', 'clipboard-check']" />
                        <span>{{ $t('task_fill') }}</span>
                      </div>
                    </template>
                    <template v-else>
                      <div class="task-status" :class="{'executed': task.execution_type === 'accept', 'ignored': task.execution_type === 'ignore'}">
                        <span v-if="task.execution_type === 'accept'"><Icon name="checked" /> {{ $t('task_executed') }}</span>
                        <span v-if="task.execution_type === 'ignore'"><Icon name="cross" /> {{ $t('task_ignored') }}</span>
                      </div>

                      <div v-if="task.event_type === 'conference'" class="action-pill primary" @click.stop="sendTask(task)">
                        <font-awesome-icon :icon="['fas', 'paper-plane']" />
                        <span>{{ $t('task_reinvite') }}</span>
                      </div>
                      <div v-if="task.event_type === 'content' || task.event_type === 'hcp-survey'" class="action-pill primary" @click.stop="sendTask(task)">
                        <font-awesome-icon :icon="['fas', 'paper-plane']" />
                        <span>{{ $t('task_resend') }}</span>
                      </div>
                      <div v-if="task.event_type === 'segment' || task.event_type === 'survey'" class="action-pill primary" @click.stop="fillFormTask(task)">
                        <font-awesome-icon :icon="['fas', 'clipboard-check']" />
                        <span>{{ $t('task_modify') }}</span>
                      </div>
                    </template>
                  </div>
                </div>

                <!-- Details Section -->
                <div class="detail-row">
                  <div v-if="task.execution_type === 'accept'" class="task-details-section">
                    <div class="expand-button" @click="toggleTaskDetails(task)">
                      <span>{{ $t('task_interaction_detail') }}</span>
                      <Icon :name="taskDetailsExpanded[getTaskKey(task)] ? 'arrow-up' : 'arrow-down'" size="12px" />
                    </div>

                    <div v-if="taskDetailsExpanded[getTaskKey(task)]" class="task-details-content">
                      <van-loading v-if="taskDetailsLoading[getTaskKey(task)]" size="20px" />
                      <div v-else-if="taskInteractionHistory[getTaskKey(task)]" class="interaction-history">
                        <div v-for="(dayGroup, dayIndex) in partitionInteractionList(taskInteractionHistory[getTaskKey(task)])" :key="dayIndex" class="day-group">
                          <div class="interaction-list">
                            <div v-for="(item, itemIndex) in dayGroup" :key="itemIndex" class="interaction-item" :class="`interaction-${item.status}`">
                              <div class="interaction-header">
                                <span class="interaction-type" :class="`type-${item.status}`">
                                  {{ item.status === '发送' ? $t('task_send') : $t('task_open') }}
                                </span>
                                <span class="interaction-datetime">
                                  {{ formatDateTime(item.send_time || item.open_time) }}
                                </span>
                                <div v-if="item.status === '打开'" class="duration-info">
                                  {{ $t('task_browse') }} <span class="duration-value">{{ formatDuration(item.duration) }}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div v-if="!taskInteractionHistory[getTaskKey(task)].length" class="no-interaction">
                          {{ $t('task_no_interaction') }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Separator -->
              <div v-if="index < group.visibleItems.length - 1" class="item-separator"></div>

            </div>
          </div>
          <div
            v-if="group.items.length > defaultGroupVisibleCount"
            class="group-collapse-toggle"
            @click="toggleTaskGroup(group.id)"
          >
            <span>
              {{ group.isExpanded ? '收起' : `展开全部（还有${group.items.length - defaultGroupVisibleCount}位医生）` }}
            </span>
            <Icon :name="group.isExpanded ? 'arrow-up' : 'arrow-down'" size="12px" />
          </div>
        </div>
      </van-list>
    </div>

    <!-- 预览弹窗保持不变 -->
    <transition name="slide-right">
      <swipeDialog
        v-if="htmlContent"
        :backtxt="$t('btn_prev')"
        :title="$t('task_content_preview')"
        @closeFn="closePreviewFn"
      >
        <div v-if="htmlContent" class="html-preview" v-html="htmlContent"></div>
      </swipeDialog>
    </transition>

    <BrintellixTypingDialog
      :show="showTypingDialog"
      :physician-id="typingDialogTask ? typingDialogTask.physician_id : ''"
      :physician-name="typingDialogTask ? typingDialogTask.physician_name : ''"
      :product-id="typingDialogTask ? typingDialogTask.product_id : ''"
      :initial-answers="typingDialogAnswers"
      @update:show="showTypingDialog = $event"
      @close="showTypingDialog = false"
      @finish="onTypingFinish"
    />

    <!-- 新增：Survey 弹窗（用于 survey 类型任务） -->
    <SurveyDialog
      :show="showSurveyDialog"
      :questionnaire-id="surveyDialogTask ? surveyDialogTask.questionnaire_id : ''"
      :physician-id="surveyDialogTask ? surveyDialogTask.physician_id : ''"
      :physician-name="surveyDialogTask ? surveyDialogTask.physician_name : ''"
      :recorder-id="userId"
      :recorder-name="userName"
      @update:show="showSurveyDialog = $event"
      @close="showSurveyDialog = false"
      @finish="onSurveyFinish"
    />
  </div>
</template>

<script>
import { DropdownMenu, DropdownItem, List, Icon, Dialog, Loading, Circle } from 'vant';
import { getTasks, executeTask, reExecuteTask, getCampaignEvents, getMyCampaignEventStat } from '@/api/nba';
import { getPhysicianContentSend, getPhysicianContentOpen } from '@/api/content';
import { wecomSDK } from '@/utils/wecom';
import { getContentById, getContentSendOpenStats } from '@/api/content';
import { isGBA } from '@/utils/index';
import swipeDialog from '@/views/layout/swipeDialog';
import BrintellixTypingDialog from './components/NBA/BrintellixTypingDialog.vue';
import SurveyDialog from './components/NBA/SurveyDialog.vue'; // 新增
import { selectRecommendedWording } from '@/utils/recommendedWording';

export default {
  name: 'TaskPage',
  components: {
    'van-dropdown-menu': DropdownMenu,
    'van-dropdown-item': DropdownItem,
    'van-list': List,
    'van-loading': Loading,
    'van-circle': Circle,
    'Icon': Icon,
    swipeDialog,
    BrintellixTypingDialog,
    SurveyDialog, // 新增
  },
  data() {
    const routeQuery = this.$route && this.$route.query ? this.$route.query : {};
    const routeCampaignEventId = routeQuery.campaign_event_id || '';
    const shouldClearEvent = routeQuery.clear_event === '1';

    return {
      selectedProduct: (routeCampaignEventId || shouldClearEvent) ? '' : (localStorage.getItem('selectedProduct') || ''),
      selectedEvent: shouldClearEvent ? '' : (routeCampaignEventId || localStorage.getItem('selectedEvent') || ''),
      portrait: require('@/assets/lundbeck/avatar-unknown.png'),
      portraitMale: require('@/assets/lundbeck/avatar-male.png'),
      portraitFemale: require('@/assets/lundbeck/avatar-female.png'),
      productOptions: [
        { text: this.$t('task_all_product'), value: '' },
        { text: 'Azilect', value: 'Azilect_000001' },
        { text: 'Brintellix', value: 'Brintellix_000001' },
        { text: 'Ebixa', value: 'Ebixa_000001' },
        { text: 'Lexapro', value: 'Lexapro_000001' },
        { text: this.$t('task_executed_task'), value: 'is_accepted' },
        { text: this.$t('task_ignored_task'), value: 'is_ignored' },
      ],
      eventOptions: [
        { text: this.$t('task_all_task'), value: '' }
      ],
      tasks: [],
      pageNum: 1,
      pageSize: 20,
      finished: false,
      listLoading: false,
      userId: '',
      userName: '', // 新增：当前用户名
      roleId: localStorage.getItem('role_id') || '', // Add roleId
      htmlContent: null,
      campaignEvents: [], // 存储任务列表
      eventTaskCounts: {}, // 存储每个任务的任务数量 { campaign_event_id: count }
      defaultGroupVisibleCount: 5,
      expandedTaskGroups: {},
      taskDetailsExpanded: {}, // 新增：记录每个任务的详情展开状态
      taskDetailsLoading: {}, // 新增：记录每个任务详情加载状态
      taskInteractionHistory: {}, // 新增：记录每个任务的互动历史
      showTypingDialog: false,
      typingDialogTask: null,
      typingDialogAnswers: null,
      showSurveyDialog: false, // 新增：Survey 弹窗
      surveyDialogTask: null, // 新增：当前 survey 任务
      quarterKey: '', targetSendCount: 150, sendCount: 0,
      currentRate: 0, // 新增：用于环形进度条动画
    };
  },
  computed: {
    // 季度完成进度
    quarterlyProgress() {
      if (!this.quarterlyTargetCount) return 0;
      const progress = Math.round((this.quarterlyCompletedCount / this.quarterlyTargetCount) * 100);
      return Math.min(progress, 100);
    },
    // 当前季度名称
    currentQuarterName() {
      const now = new Date();
      const month = now.getMonth();
      const q = Math.floor(month / 3) + 1;
      return `${now.getFullYear()}年 Q${q}`;
    },
    // 季度目标
    quarterlyTargetCount() {
      return this.targetSendCount || 0;
    },
    // 季度已完成
    quarterlyCompletedCount() {
      return this.sendCount || 0;
    },
    // 季度截止日期
    quarterEndDate() {
      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth();
      const qEndMonth = Math.floor(month / 3) * 3 + 2; // 2, 5, 8, 11
      const endDate = new Date(year, qEndMonth + 1, 0); // last day of qEndMonth
      return `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
    },
    // 新增：任务分组
    groupedTasks() {
      const groups = [];
      const map = new Map();

      this.tasks.forEach(task => {
        if (!map.has(task.campaign_event_id)) {
          // 获取推荐理由
          const eventInfo = this.campaignEvents ? this.campaignEvents.find(e => e.campaign_event_id === task.campaign_event_id) : null;
          let reasonTarget = '';
          let reasonPrior = '';

          if (eventInfo) {
            const targetFields = [
              eventInfo.target_physician_segment,
              eventInfo.target_physician_class,
              eventInfo.target_physician_area,
              eventInfo.target_physician_tag,
              eventInfo.target_physician_roster
            ].filter(item => item && item.trim() !== '');

            const productName = eventInfo.product_name || task.product_name || '';
            reasonTarget = productName ? `${productName}目标医生` : '目标医生';
            if (targetFields.length > 0) {
              reasonTarget += `：${targetFields.join('，')}`;
            }

            const priorFields = [
              eventInfo.prior_physician_segment,
              eventInfo.prior_physician_class,
              eventInfo.prior_physician_area,
              eventInfo.prior_physician_tag,
              eventInfo.prior_physician_roster
            ].filter(item => item && item.trim() !== '');

            if (priorFields.length > 0) {
              reasonPrior = `优先推荐${priorFields.join('，')}`;
            }
          } else {
            const productName = task.product_name || '';
            reasonTarget = productName ? `${productName}目标医生` : '目标医生';
          }

          const group = {
            id: task.campaign_event_id,
            campaign_event_name: task.campaign_event_name,
            event_type: task.event_type,
            product_name: task.product_name,
            event_end_date: task.event_end_date,
            event_start_date: task.event_start_date,
            items: [],
            reasonTarget,
            reasonPrior
          };
          map.set(task.campaign_event_id, group);
          groups.push(group);
        }
        map.get(task.campaign_event_id).items.push(task);
      });

      return groups.map((group) => {
        const isExpanded = !!this.expandedTaskGroups[group.id];
        return {
          ...group,
          isExpanded,
          visibleItems: isExpanded ? group.items : group.items.slice(0, this.defaultGroupVisibleCount),
        };
      });
    },
    // 修改：计算带有数量徽章的任务选项，在文本中显示数量
    eventOptionsWithCount() {
      return this.eventOptions.map(option => {
        if (option.value === '') {
          // 计算所有任务的总数
          const totalCount = Object.values(this.eventTaskCounts).reduce((sum, count) => sum + count, 0);
          return {
            ...option,
            text: totalCount > 0 ? `${this.$t('task_all_task')} (${totalCount})` : this.$t('task_all_task')
          };
        } else {
          const count = this.eventTaskCounts[option.value] || 0;
          return {
            ...option,
            text: count > 0 ? `${option.originalText} (${count})` : option.originalText
          };
        }
      });
    }
  },
  watch: {
    '$route.query.campaign_event_id'(newVal) {
      if (newVal) {
        this.selectedProduct = '';
        this.selectedEvent = newVal;
        localStorage.setItem('selectedProduct', '');
        localStorage.setItem('selectedEvent', newVal);
        this.loadCampaignEvents();
        this.resetAndLoad();
      }
    },
    '$route.query.clear_event'(newVal) {
      if (newVal === '1') {
        this.selectedProduct = '';
        this.selectedEvent = '';
        localStorage.setItem('selectedProduct', '');
        localStorage.setItem('selectedEvent', '');
        this.resetAndLoad();
      }
    },
    selectedProduct(newVal) {
      localStorage.setItem('selectedProduct', newVal);
      this.resetAndLoad();
      // 当产品变化时，重新加载任务选项
      this.loadCampaignEvents();
      this.fetchQuarterlyStats();
    },
    selectedEvent(newVal) {
      localStorage.setItem('selectedEvent', newVal);
      this.resetAndLoad();
    }
  },
  created() {
    this.userId = localStorage.getItem('user_id') || '';
    this.userName = localStorage.getItem('user_name') || ''; // 新增
    if (this.$route.query.campaign_event_id) {
      localStorage.setItem('selectedProduct', '');
      localStorage.setItem('selectedEvent', this.$route.query.campaign_event_id);
    } else if (this.$route.query.clear_event === '1') {
      localStorage.setItem('selectedProduct', '');
      localStorage.setItem('selectedEvent', '');
    }
    this.loadCampaignEvents();
    this.fetchQuarterlyStats();
  },
  methods: {
    async fetchQuarterlyStats() {
      try {
        const now = new Date();
        const y = now.getFullYear();
        const m = now.getMonth();
        const q = Math.floor(m / 3) + 1;
        const quarterStr = y + 'Q' + q;

        const res = await getContentSendOpenStats({ quarter: quarterStr, user_id: this.userId });
        if (res && res.data) {
          this.targetSendCount = res.data.target_send_count || 150;
          this.sendCount = res.data.send_count || 0;
        }
      } catch (e) {
        // 获取数据失败，保持默认值
      }
    },
    // 返回事件类型文案，使用 switch 便于扩展
    eventTypeText(type) {
      switch (type) {
        case 'conference':
          return this.$t('task_live');
        case 'segment':
        case 'survey':
          return this.$t('task_survey');
        case 'hcp-survey':
          return this.$t('task_doctor_survey');
        case 'visit':
        case 'call':
          return this.$t('task_visit');
        case 'content':
          return this.$t('task_tweet');
        default:
          return this.$t('task_other');
      }
    },
    // 新增：onLoad 方法
    onLoad() {
      if (!this.finished) {
        this.loadTasks();
      }
    },

    // 新增：formatDate 方法
    formatDate(dateStr) {
      if (!dateStr) return '';
      return dateStr.split(' ')[0];
    },

    // 新增：获取事件显示文本
    getEventDisplayText() {
      if (!this.selectedEvent) {
        return this.$t('task_all_task');
      }
      const selectedOption = this.eventOptions.find(option => option.value === this.selectedEvent);
      return selectedOption ? selectedOption.text : this.$t('task_all_task');
    },

    // 新增：获取任务总数
    getTotalTaskCount() {
      if (!this.selectedEvent) {
        // 显示所有任务的总数
        return Object.values(this.eventTaskCounts).reduce((sum, count) => sum + count, 0);
      } else {
        // 显示选中任务的任务数
        return this.eventTaskCounts[this.selectedEvent] || 0;
      }
    },

    // 修改：加载任务事件列表并获取任务数量
    async loadCampaignEvents() {
      try {
        let selectedProduct = this.selectedProduct;
        if (this.selectedProduct === 'is_accepted' || this.selectedProduct === 'is_ignored') {
          selectedProduct = ''; // 清除产品筛选
        }
        const res = await getCampaignEvents(this.userId, selectedProduct, this.roleId);
        if (res && res.data && Array.isArray(res.data)) {
          this.campaignEvents = res.data;

          // 构建任务下拉选项，保存原始文本
          this.eventOptions = [
            { text: this.$t('task_all_task'), value: '', originalText: this.$t('task_all_task') },
            ...this.campaignEvents.map(event => ({
              text: event.campaign_event_name,
              value: String(event.campaign_event_id),
              originalText: event.campaign_event_name
            }))
          ];

          // 如果当前选中的任务不在新的列表中，重置为全部
          if (this.selectedEvent && !this.campaignEvents.find(e => String(e.campaign_event_id) === String(this.selectedEvent))) {
            this.selectedEvent = '';
            localStorage.setItem('selectedEvent', '');
          }

          // 加载每个任务的任务数量
          await this.loadEventTaskCounts();
        } else {
          this.eventOptions = [{ text: this.$t('task_all_task'), value: '' }];
        }
      } catch (error) {
        console.error('Failed to load campaign events:', error);
        this.eventOptions = [{ text: this.$t('task_all_task'), value: '' }];
      }
    },

    canUseCampaignEventStat() {
      return !this.selectedProduct || this.selectedProduct === 'is_accepted' || this.selectedProduct === 'is_ignored';
    },

    getStatCountByFilter(event) {
      if (this.selectedProduct === 'is_accepted') {
        return event.accept_task_count || 0;
      }
      if (this.selectedProduct === 'is_ignored') {
        return event.ignore_task_count || 0;
      }
      return event.unexecuted_task_count || 0;
    },

    async loadEventTaskCountsFromStat() {
      const res = await getMyCampaignEventStat();
      const stat = res && res.data ? res.data : {};
      const campaignEvents = Array.isArray(stat.campaign_events) ? stat.campaign_events : [];
      const statMap = new Map(campaignEvents.map(event => [event.campaign_event_id, event]));
      const nextCounts = {};

      this.campaignEvents.forEach(event => {
        if (event && event.campaign_event_id) {
          nextCounts[event.campaign_event_id] = this.getStatCountByFilter(statMap.get(event.campaign_event_id) || {});
        }
      });

      this.eventTaskCounts = nextCounts;
    },

    async loadEventTaskCountsByTaskApi() {
      let selectedProduct = this.selectedProduct;
      let isExecuted = false;
      if (this.selectedProduct === 'is_accepted' || this.selectedProduct === 'is_ignored') {
        isExecuted = true;
        selectedProduct = ''; // 清除产品筛选
      }

      // 并发获取每个任务的任务数量
      const countPromises = this.campaignEvents.map(async (event) => {
        try {
          // 获取任务数量（这里假设 getTasks 返回的 data_count 字段包含总数）
          const res = await getTasks(
            this.userId,
            selectedProduct,
            event.campaign_event_id,
            isExecuted,
            this.selectedProduct === 'is_accepted' ? 'accept' : this.selectedProduct === 'is_ignored' ? 'ignore' : '',
            1,
            1, // 只需要获取数量，不需要具体数据
            this.roleId
          );
          return {
            eventId: event.campaign_event_id,
            count: res?.data_count || 0
          };
        } catch (error) {
          console.error(`Failed to get task count for event ${event.campaign_event_id}:`, error);
          return {
            eventId: event.campaign_event_id,
            count: 0
          };
        }
      });

      const results = await Promise.all(countPromises);
      const nextCounts = {};

      results.forEach(result => {
        nextCounts[result.eventId] = result.count;
      });

      this.eventTaskCounts = nextCounts;
    },

    // 新增：加载每个任务的任务数量
    async loadEventTaskCounts() {
      try {
        if (this.canUseCampaignEventStat()) {
          await this.loadEventTaskCountsFromStat();
        } else {
          await this.loadEventTaskCountsByTaskApi();
        }
      } catch (error) {
        console.error('Failed to load event task counts:', error);
        await this.loadEventTaskCountsByTaskApi();
      }
    },

    // 新增：重置并加载数据的通用方法
    resetAndLoad() {
      this.pageNum = 1;
      this.tasks = [];
      this.finished = false;
      this.loadTasks();
    },

    // 修改：loadTasks 方法，添加任务筛选
    async loadTasks() {
      this.listLoading = true;
      try {
        let selectedProduct = this.selectedProduct;
        let isExecuted = false;
        if (this.selectedProduct === 'is_accepted' || this.selectedProduct === 'is_ignored') {
          isExecuted = true;
          selectedProduct = ''; // 清除产品筛选
        }

        // 传入选中的任务事件ID
        const res = await getTasks(
          this.userId,
          selectedProduct,
          this.selectedEvent, // 传入选中的任务事件ID
          isExecuted,
          this.selectedProduct === 'is_accepted' ? 'accept' : this.selectedProduct === 'is_ignored' ? 'ignore' : '',
          this.pageNum,
          this.pageSize,
          this.roleId
        );

        if (res && res.data && Array.isArray(res.data)) {
          if (this.pageNum === 1) {
            this.tasks = res.data;
          } else {
            this.tasks = this.tasks.concat(res.data);
          }
          this.pageNum++;
          if (res.data.length < this.pageSize) {
            this.finished = true;
          }
        } else {
          this.finished = true;
        }
      } catch (e) {
        this.finished = true;
      }
      this.listLoading = false;
    },

    // 修改：在任务操作成功后更新数量
    async updateTaskCountAfterOperation(campaignEventId) {
      try {
        if (this.canUseCampaignEventStat()) {
          await this.loadEventTaskCountsFromStat();
          return;
        }

        let selectedProduct = this.selectedProduct;
        if (this.selectedProduct === 'is_accepted' || this.selectedProduct === 'is_ignored') {
          selectedProduct = '';
        }

        const res = await getTasks(
          this.userId,
          selectedProduct,
          campaignEventId,
          false,
          '',
          1,
          1,
          this.roleId
        );

        this.eventTaskCounts[campaignEventId] = res?.data_count || 0;
      } catch (error) {
        console.error('Failed to update task count:', error);
      }
    },

    // 修改：ignoreTask 方法，操作成功后更新数量
    ignoreTask(task) {
      Dialog.confirm({
        title: this.$t('task_confirm_operation'),
        message: this.$t('task_confirm_ignore'),
        confirmButtonText: this.$t('confirm_txt'),
        cancelButtonText: this.$t('cancel_txt'),
      })
        .then(() => {
          executeTask('ignore', task.campaign_event_id, task.physician_id, this.userId, localStorage.getItem('user_name') || '', localStorage.getItem('territory_id') || '', this.roleId)
            .then(async () => {
              this.tasks = this.tasks.filter(
                t => !(t.campaign_event_id === task.campaign_event_id && t.physician_id === task.physician_id)
              );
              this.$toast(this.$t('task_ignore_success'));
              this.$root.$emit('updateTaskCount');

              // 更新对应任务的任务数量
              await this.updateTaskCountAfterOperation(task.campaign_event_id);
            })
            .catch(error => {
              console.error('Failed to ignore task:', error);
              this.$toast(this.$t('task_ignore_fail'));
            });
        })
        .catch(() => {
          // 用户取消，不做处理
        });
    },

    async fillFormTask(task) {
      // 区分 segment / survey
      if (task.event_type === 'survey') {
        this.surveyDialogTask = task;
        this.showSurveyDialog = true;
        return;
      }
      if (task.event_type === 'segment') {
        this.typingDialogTask = task;
        this.typingDialogAnswers = null;
        this.showTypingDialog = true;
      }
    },

    async onTypingFinish({ resultType, answers }) {
      if (this.typingDialogTask) {
        await this.executeSegmentTask(this.typingDialogTask);
        this.showTypingDialog = false;
        this.typingDialogTask = null;
        this.typingDialogAnswers = null;
        this.resetAndLoad();
      }
    },

    async onSurveyFinish({ questionnaireId, answers }) { // 新增
      if (this.surveyDialogTask) {
        await this.executeSegmentTask(this.surveyDialogTask); // 复用标记已执行
        this.showSurveyDialog = false;
        this.surveyDialogTask = null;
        this.resetAndLoad();
      }
    },

    async executeSegmentTask(task) {
      try {
        await executeTask('accept', task.campaign_event_id, task.physician_id, this.userId, localStorage.getItem('user_name') || '', localStorage.getItem('territory_id') || '', this.roleId);
        this.$toast(this.$t('task_execute_success'));
        this.$root.$emit('updateTaskCount');
        await this.updateTaskCountAfterOperation(task.campaign_event_id);
      } catch (error) {
        this.$toast(this.$t('task_execute_fail'));
      }
    },
    async sendTask(task) {
      const toast = this.$createToast({
        time: 10000,
        mask: false,
      });

      toast.show();
      const res = await getContentById(task.content_id);
      if (res && res.value && res.value.length > 0) {
        const forwardContent = res.value[0];

        try {
          if (forwardContent.recommended_wording) {
            try {
              const wordingToCopy = await selectRecommendedWording(forwardContent.recommended_wording, {
                title: this.$t('task_suggest_wording'),
                confirmButtonText: this.$t('task_copy_wording'),
                cancelButtonText: this.$t('task_skip'),
              });

              if (wordingToCopy) {
                const success = await wecomSDK.copyToClipboard(wordingToCopy);
                if (!success) {
                  this.$toast.fail(this.$t('task_copy_fail'));
                }
              }
            } catch (err) {
              console.log(err);
            }
          }
          toast.show();

          const success = await wecomSDK.shareWechatMessage({
            content_id: task.content_id,
            sender_user_id: localStorage.getItem('user_id'),
            sender_user_name: localStorage.getItem('user_name'),
            sender_territory_id: localStorage.getItem('territory_id'),
            receiver_physician_id: task.physician_id,
            send_time: new Date(),
            title: forwardContent.content_name,
            description: forwardContent.description,
            wx_logo: forwardContent.wx_logo,
          });

          if (success) {
            console.log('Send task:', task);
            if (task.campaign_event_execution_id) {
              reExecuteTask(task.campaign_event_execution_id, 'accept', task.campaign_event_id, task.physician_id, this.userId, localStorage.getItem('user_name') || '', localStorage.getItem('territory_id') || '', this.roleId)
                .then(async () => {
                  this.pageNum = 1;
                  this.tasks = [];
                  this.finished = false;
                  this.loadTasks();
                  this.$toast(this.$t('task_resend_success'));
                  this.$root.$emit('updateTaskCount');

                  // 更新对应任务的任务数量
                  await this.updateTaskCountAfterOperation(task.campaign_event_id);
                })
                .catch(error => {
                  console.error('Failed to re-execute task:', error);
                  this.$toast(this.$t('task_resend_fail'));
                });
            } else {
              executeTask('accept', task.campaign_event_id, task.physician_id, this.userId, localStorage.getItem('user_name') || '', localStorage.getItem('territory_id') || '', this.roleId)
                .then(async () => {
                  this.tasks = this.tasks.filter(
                    t => !(t.campaign_event_id === task.campaign_event_id && t.physician_id === task.physician_id)
                  );
                  this.$toast(this.$t('task_execute_success'));
                  this.$root.$emit('updateTaskCount');

                  // 更新对应任务的任务数量
                  await this.updateTaskCountAfterOperation(task.campaign_event_id);
                })
                .catch(error => {
                  console.error('Failed to accept task:', error);
                  this.$toast(this.$t('task_send_fail'));
                });
            }
          }
          toast.hide();
        } catch (err) {
          toast.hide();
          console.error('Forward error:', err);
          this.$toast.fail(this.$t('task_forward_fail'));
        }
      } else {
        toast.hide();
      }
    },
    goToDetail(task) {
      this.$router.push({
        path: (isGBA() ? '/gba' : '/hcp') + '/detail',
        query: {
          physician_id: task.physician_id,
        }
      });
    },
    goToSummaryDetail() {
      this.$router.push((isGBA() ? '/gba' : '/hcp') + '/task-summary-detail');
    },
    goToContent() {
      const path = isGBA() ? '/gba/content' : '/hcp/content';
      this.$router.push({
        path: path,
      });
    },
    planVisit(task) {
      const path = isGBA() ? '/gba/visit-create' : '/hcp/visit-create';
      this.$router.push({
        path: path,
        query: {
          physician_id: task.physician_id
        }
      });
    },
    async previewTask(task) {
      if (task.content_id) {
        const toast = this.$createToast({
          time: 10000,
          mask: false,
        });
        toast.show();
        const res = await getContentById(task.content_id);
        if (res && res.value && res.value.length > 0) {
          const previewContent = res.value[0];
          this.htmlContent = previewContent.content_body;
          this.$store.dispatch('setDialogStatus', true);
        }
        toast.hide();
      }
    },
    closePreviewFn() {
      this.$store.dispatch('setDialogStatus', false);
      this.htmlContent = null;
    },
    toggleTaskGroup(groupId) {
      this.$set(this.expandedTaskGroups, groupId, !this.expandedTaskGroups[groupId]);
    },
    // 新增：切换任务详情展开状态
    async toggleTaskDetails(task) {
      const taskKey = this.getTaskKey(task);

      if (this.taskDetailsExpanded[taskKey]) {
        // 如果已展开，则收起
        this.$set(this.taskDetailsExpanded, taskKey, false);
      } else {
        // 如果未展开，则展开并加载数据
        this.$set(this.taskDetailsExpanded, taskKey, true);

        if (!this.taskInteractionHistory[taskKey]) {
          await this.loadTaskInteractionHistory(task);
        }
      }
    },

    // 新增：生成任务唯一键
    getTaskKey(task) {
      return `${task.campaign_event_id}_${task.physician_id}`;
    },

    // 修改：加载任务的互动历史
    async loadTaskInteractionHistory(task) {
      const taskKey = this.getTaskKey(task);
      this.$set(this.taskDetailsLoading, taskKey, true);

      try {
        // 修改：检查函数是否存在，如果不存在则使用备用方案
        let sendData = [];
        let openData = [];

        const sendRes = await getPhysicianContentSend(task.physician_id, task.content_id);
        sendData = sendRes?.value || sendRes?.data || [];

        const openRes = await getPhysicianContentOpen(task.physician_id, task.content_id);
        openData = openRes?.value || openRes?.data || [];

        // 合并数据并添加类型标识
        const interactionHistory = [
          ...sendData.map(item => ({
            ...item,
            type: '发送',
            status: '发送'
          })),
          ...openData.map(item => ({
            ...item,
            type: '打开',
            status: '打开'
          }))
        ];

        // 按时间排序（最新的在前）
        interactionHistory.sort((a, b) => {
          const timeA = new Date(a.send_time || a.open_time).getTime();
          const timeB = new Date(b.send_time || b.open_time).getTime();
          return timeB - timeA;
        });

        this.$set(this.taskInteractionHistory, taskKey, interactionHistory);
      } catch (error) {
        console.error('Failed to load task interaction history:', error);
        this.$set(this.taskInteractionHistory, taskKey, []);
      } finally {
        this.$set(this.taskDetailsLoading, taskKey, false);
      }
    },

    // 修改：按日期分组互动记录（不再按日期分组）
    partitionInteractionList(list) {
      // 直接返回一个包含所有数据的数组，不进行日期分组
      return [list.map((item) => ({
        ...item,
        send_time: item.send_time || item.open_time,
      }))];
    },

    // 新增：格式化日期时间（合并显示）
    formatDateTime(dateTimeStr) {
      if (!dateTimeStr) return '';

      const date = new Date(dateTimeStr);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');

      return `${year}/${month}/${day} ${hours}:${minutes}`;
    },

    // 修改：格式化时长（分钟:秒）
    formatDuration(seconds) {
      if (!seconds || seconds === 0) return '0:00';

      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;

      return `${minutes}:${String(remainingSeconds).padStart(2, '0')}`;
    },
    parseProducts(productsStr, currentProductId) {
      try {
        const products = productsStr ? JSON.parse(productsStr) : [];
        // 只显示与当前 task.product_id 一致的分型
        const filtered = products.filter(
          p => p.product_id === currentProductId
        );
        return filtered.sort((a, b) => {
          const nameA = a.product_name ? a.product_name.toLowerCase() : '';
          const nameB = b.product_name ? b.product_name.toLowerCase() : '';
          if (nameA < nameB) return -1;
          if (nameA > nameB) return 1;
          return 0;
        });
      } catch (e) {
        return [];
      }
    },
  }
};
</script>

<!-- 样式保持不变 -->
<style lang="scss" scoped>
.task-page-container {
    height: 100vh;
    overflow: hidden;
    background: #f2f5f9;
  }
  .task-page {
    padding: 16px 10px;
    background: #f2f5f9;
    text-align: left;
    height: 100%;
    overflow-y: auto;

  // 季度目标卡片样式
  .summary-card {
    background: #fff;
    border-radius: 12px;
    padding: 12px 16px;
    margin-bottom: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

    .summary-top {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;

      .title-row {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
      }

      .summary-title {
        font-size: 16px;
        font-weight: 600;
        color: #2c3034;
        margin-bottom: 2px;
      }

      .detail-btn {
        display: flex;
        align-items: center;
        font-size: 11px;
        color: #aa341b;
        cursor: pointer;
        padding: 2px 0;

        .van-icon {
          margin-left: 2px;
          font-size: 10px;
        }
      }

      .summary-date {
        font-size: 11px;
        color: #aa341b;
        background: rgba(170, 52, 27, 0.08);
        padding: 4px 8px;
        border-radius: 12px;
        font-weight: 500;
        margin-left: 8px;
        white-space: nowrap;
        align-self: flex-start;
      }
    }

    .summary-metrics {
      display: flex;
      align-items: center;
      justify-content: space-between;

      .metric-item {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        flex: 1;

        .metric-value {
          font-size: 20px;
          font-weight: 700;
          color: #2c3034;
          line-height: 1.2;
        }

        .metric-label {
          font-size: 11px;
          color: #999;
          margin-top: 4px;
        }
      }

      .metric-divider {
        width: 1px;
        height: 24px;
        background-color: #eee;
        margin: 0 16px;
      }

      .metric-chart {
        flex-shrink: 0;
        margin-left: 12px;

        ::v-deep .van-circle__text {
          color: #aa341b;
          font-size: 12px;
          font-weight: 600;
        }
      }
    }

    .summary-note {
      font-size: 11px;
      color: #999;
      margin-bottom: 10px;
    }
  }

  .header-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;

    .title-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
      gap: 12px;
    }

    .page-title {
      font-size: 20px;
      font-weight: 500;
      color: #2c3034;
      padding-left: 4px;
      height: 24px;
      flex-shrink: 0;
    }
  }

  .filter-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    // background: #fff;
    border-radius: 20px;
    // padding: 0 12px;
    // margin-bottom: 12px;

    ::v-deep .van-dropdown-menu__title {
        font-size: 13px;
      }

      .product-filter {
        min-width: 120px;
        flex: 1;

        ::v-deep .van-dropdown-menu__item {
          justify-content: flex-start;
        }

        .van-dropdown-menu {
          width: 100%;
        }
      }

      .event-filter {
        min-width: 120px;
        flex: 2;
        text-align: right;

        ::v-deep .van-dropdown-menu__item {
          justify-content: flex-end;
        }

        .van-dropdown-menu {
          width: 100%;
        }

        .event-dropdown-title {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 8px;
          width: 100%;

          .task-title {
            width: 100%;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          .task-count-badge {
            background: #aa341b;
            color: white;
            border-radius: 40px;
            min-width: 20px;
            height: 20px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 12px;
            font-weight: 600;
            flex-shrink: 0;
            padding: 0 2px;
          }
        }
      }
    }

  .view-all-articles {
    display: flex;
    align-items: center;
    gap: 2px;
    font-size: 13px;
    color: #aa341b;
    font-weight: 500;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 4px;
    transition: background-color 0.2s;
    white-space: nowrap;
    flex-shrink: 0;

    &:active {
      background-color: rgba(170, 52, 27, 0.1);
    }

    .icon-circle {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 18px;
      height: 18px;
      border-radius: 50%;
      background-color: #aa341b;
      color: white;
      margin-right: 2px;

      .van-icon {
        font-size: 12px;
      }
    }
  }

  .task-list {
    padding-bottom: 80px;

    .task-group-card {
      background: #fff;
      border-radius: 20px;
      margin-bottom: 32px;
      overflow: hidden;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
      border: 1px solid #f0f0f0;

      .group-header {
        padding: 12px 16px;
        background: linear-gradient(180deg, #ffffff 0%, #f4f4f4 100%);
        border-bottom: 1px solid #eee;
        display: flex;
        flex-direction: column;
        border-radius: 0;

        .header-row-1 {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 6px;
          font-size: 11px;

          .left-badges {
            display: flex;
            align-items: center;
            gap: 6px;

            .task-type {
              padding: 4px 10px 3px;
              border-radius: 24px;
              font-size: 11px;
              font-weight: 600;
              background-color: #f2ede4;
              color: #555;
              white-space: nowrap;
            }

            .product-tag {
              display: inline-block;
              padding: 3px 6px 2px;
              font-size: 11px;
              color: #333;
              font-weight: 500;
            }
          }

          .right-date {
            color: #999;
            font-size: 11px;
          }
        }

        .header-row-2 {
          display: flex;
          align-items: flex-start;
          margin-top: 4px;
          margin-bottom: 12px;
          justify-content: space-between;

          .group-title {
            font-size: 16px;
            font-weight: 600;
            color: #2c3034;
            line-height: 1.4;
            flex: 1;
            margin-right: 8px;
          }

          .preview-btn {
            flex-shrink: 0;
            display: flex;
            align-items: center;
            color: #aa341b;
            font-size: 11px;
            padding: 3px 8px 2px;
            border: 1px solid #aa341b;
            border-radius: 16px;
            cursor: pointer;
            margin-top: 0; // Align with text

            .van-icon {
              margin-right: 4px;
              font-size: 12px;
            }

            &:active {
              opacity: 0.8;
            }
          }
        }

        .header-row-3 {
           font-size: 11px;
           color: #666;
           display: flex;
           align-items: flex-start;

           .reason-label {
             color: #666;
             font-weight: 500;
             white-space: nowrap;
           }

           .reason-star-icon {
             font-size: 10px;
             margin: 0 1px;
             ::v-deep path {
               fill: url(#star-gradient);
             }
           }

           .reason-text {
             color: #666;
             margin-left: 2px;
           }
        }
      }

      .group-items {
        .physician-item {
          padding: 8px 16px 10px;
          position: relative;

          .ignore-btn-icon {
            position: absolute;
            top: -6px;
            right: -4px;
            padding: 8px;
            color: #ccc;
            font-size: 15px;
          }

          .item-main-row {
            display: flex;
            align-items: center;
          }

          .avatar-column {
            position: relative;
            margin-right: 12px;
            width: 35px;
            height: 35px;
            border-radius: 50%;
            background: linear-gradient(165deg, #dce1f0, #f6eff1);
            display: flex;
            justify-content: center;
            align-items: center;

            .avatar {
              width: 35px;
              height: 35px;
              object-fit: cover;
              opacity: 0.5;
            }

            .priority-overlay {
              position: absolute;
              top: -5px;
              left: -5px;
              width: 16px;
              height: 16px;
              display: flex;
              align-items: center;
              justify-content: center;

              .priority-icon {
                font-size: 16px;

                ::v-deep path {
                  fill: url(#star-gradient);
                  stroke: #fff;
                  stroke-width: 20px;
                }
              }
            }
          }

          .physician-info-wrapper {
             flex: 1;
             margin-right: 12px;

             .user-row {
                display: flex;
                align-items: center;
                flex-wrap: wrap;
                margin-bottom: 2px;

                .fav-star {
                    color: #aa341b;
                    margin-right: 4px;
                    font-size: 14px;
                }

                .user-name {
                    font-size: 17px;
                    font-weight: 400;
                    color: #333;
                    margin-right: 6px;
                    cursor: pointer;
                }

                .weixin-icon {
                    width: 14px;
                    height: 14px;
                }

                .product-badge {
                  display: flex;
                  flex-direction: column;
                  background: linear-gradient(180deg, #fcfcfc 0%, #f3efe6 100%);
                  border: 1px solid #dccdb6;
                  border-radius: 6px;
                  padding: 2px 4px;
                  margin-left: 6px;
                  text-align: center;
                  min-width: 32px;
                  transform: scale(0.75);

                  .product-badge-top {
                    display: flex;
                    align-items: flex-end;
                    justify-content: center;
                    margin-bottom: 2px;
                  }

                  .segment-circle {
                    width: 14px;
                    height: 14px;
                    border-radius: 50%;
                    background-color: #8b7d6b;
                    color: #fff;
                    font-size: 10px;
                    font-weight: bold;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    flex-shrink: 0;
                  }

                  .segment-text {
                    margin-left: 2px;
                    font-size: 9px;
                    color: #4a4a4a;
                    font-weight: bold;
                    white-space: nowrap;
                  }
                  .product-name {
                    font-size: 8px;
                    color: #4a4a4a;
                    line-height: normal;
                  }
                }
             }

             .hospital-row {
                font-size: 11px;
                color: #b2b2b2;
                line-height: 1.2;

                .dept {
                   color: #888;
                   margin-bottom: 2px;
                }
             }
          }

          .action-column {
             display: flex;
             flex-direction: row;
             align-items: center;
             gap: 10px;
             margin-right: 10px;

             .action-item {
               display: flex;
               flex-direction: column;
               align-items: center;
               cursor: pointer;

               .action-pill {
                 margin-bottom: 2px;
               }

               .action-label {
                 font-size: 8px;
                 transform: scale(0.9);
                 color: #999;
               }

               &:active {
                 opacity: 0.8;
               }
             }

             .action-pill {
               display: flex;
               align-items: center;
               padding: 3px 10px;
               border-radius: 20px;
               color: white;
               font-size: 12px;
               font-weight: 500;
               cursor: pointer;
               height: 24px;
               box-sizing: border-box;
               transition: all 0.2s;

               svg {
                 margin-right: 3px;
                 font-size: 10px;
               }

               &.success {
                 background-color: #07c160;
               }

               &.primary {
                 background: linear-gradient(165deg, #d2563d, #aa341b);
               }

               &.gray {
                 background-color: #999;
               }

               &.outline {
                 background-color: transparent;
                 border: 1px solid currentColor;
                 box-shadow: none;
                 padding: 3px 11px; // Adjust for border

                 &.success {
                   color: #07c160;
                   background-color: rgba(7, 193, 96, 0.05);
                 }
                 &.primary {
                   color: #aa341b;
                   background-color: transparent;
                 }
                 &.gray {
                   color: #999;
                   background-color: transparent;
                 }
               }

               &:active {
                 opacity: 0.8;
                 transform: scale(0.98);
               }
             }
          }
        }

        .item-separator {
            height: 1px;
            background-color: #f5f5f5;
            margin: 0 4px;
          }
        }
      }

      .group-collapse-toggle {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 4px;
        min-height: 40px;
        border-top: 1px solid #f0f0f0;
        background: #fff;
        color: #aa341b;
        font-size: 13px;
        font-weight: 500;
        cursor: pointer;

        &:active {
          background: #faf6f5;
        }
      }
    }
  }
::v-deep .van-dropdown-menu__bar {
  background: none;
  box-shadow: none;
}

.fav-hcp {
  font-size: 22px;
  color: #aa341b;
  margin-left: 8px;
}
.task-status {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 6px;
  border-radius: 16px;
  font-size: 11px;
  font-weight: 500;
  background: #f2f5f9;
  color: #bbb;
  margin-bottom: 0;

  .van-icon {
    font-size: 11px;
  }
}
.task-status.executed {
  color: #0cc160;
  background: #f0fbf6;
}
.task-status.ignored {
  color: #999;
  background: #f2f5f9;
}

.slide-right-enter-active,
.slide-right-leave-active {
  transition: transform 0.3s ease;
}
.slide-right-enter,
.slide-right-leave-to {
  transform: translateX(100%);
}
.html-preview {
  padding: 0;
  max-width: 100%;
  overflow: auto;
  ::v-deep img {
    width: 100%;
    height: auto;
    display: block;
  }
}

.task-details-section {
  margin-top: 0;
  padding-top: 0;

  .expand-button {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
    cursor: pointer;
    padding: 4px 0;
    font-size: 12px;
    color: #666;
    transition: color 0.2s;

    &:hover {
      color: #aa341b;
    }

    .van-icon {
      font-size: 12px;
      color: #999;
      transition: color 0.2s;
    }

    &:hover .van-icon {
      color: #aa341b;
    }
  }

  .task-details-content {
    margin-top: 8px;
    padding: 8px 0;
    border-top: 1px solid #f0f0f0;

    .interaction-history {
      margin-top: 8px;

      .day-group {
        .interaction-list {
          .interaction-item {
            margin-bottom: 8px;
            padding: 6px 12px;
            background: #f8f9fa;
            border-radius: 4px;
            border-left: 3px solid #ddd;

            &.interaction-发送 {
              border-left-color: #0cc160;
              background: #f0fbf6;
            }

            &.interaction-打开 {
              border-left-color: #aa341b;
              background: #fff5f5;
            }

            .interaction-header {
              display: flex;
              align-items: center;
              gap: 8px;
              font-size: 12px;

              .interaction-type {
                padding: 4px 8px;
                border-radius: 12px;
                font-size: 10px;
                font-weight: 500;
                flex-shrink: 0;

                &.type-发送 {
                  background: #0cc160;
                  color: white;
                }

                &.type-打开 {
                  background: #aa341b;
                  color: white;
                }
              }

              .interaction-datetime {
                color: #666;
                flex-shrink: 0;
              }

              .duration-info {
                margin-left: auto;
                font-size: 11px;
                color: #666;

                .duration-value {
                  color: #aa341b;
                  font-weight: 500;
                }
              }
            }
          }
        }
      }

      .no-interaction {
        text-align: center;
        color: #999;
        font-size: 12px;
        padding: 20px;
      }
    }
  }
}

</style>
